import React, { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContainerSlotPropsOverrides,
  DialogContent,
  DialogOwnerState,
  DialogTitle,
  IconButton,
  SlotProps,
} from '@mui/material'

interface CustomDialogProps {
  children: ReactElement | ReactElement[]
  isOpen: boolean
  title: string
  hideActions?: boolean
  hideCloseButton?: boolean
  containerProps?: SlotProps<
    'div',
    DialogContainerSlotPropsOverrides,
    DialogOwnerState
  >
  paperStyle?: React.CSSProperties
  handleClose?: () => void
  handleSubmit?: (formData: any) => void
}

const CustomDialog: React.FC<CustomDialogProps> = ({
  children,
  isOpen,
  title,
  hideActions,
  hideCloseButton,
  containerProps,
  paperStyle,
  handleClose,
  handleSubmit,
}) => {
  const [formSubmited, setFormSubmited] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setFormSubmited(false)
    }
  }, [isOpen])

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={hideCloseButton ? handleClose : () => {}}
        slotProps={{
          container: containerProps,
          paper: {
            component: 'form',
            onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault()
              const formData = new FormData(event.currentTarget)
              const formJson = Object.fromEntries((formData as any).entries())
              handleSubmit?.(formJson)
              setFormSubmited(true)
            },
            style: paperStyle
              ? paperStyle
              : {
                  borderRadius: '0.6rem',
                  padding: '1rem 1.5rem 1.5rem 1.5rem',
                  boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.25)',
                },
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: '0',
            fontSize: '14px',
            fontWeight: 500,
            color: '#04002E',
            mb: title ? '6px' : 0,
          }}
        >
          {title}
        </DialogTitle>
        {!hideCloseButton && (
          <IconButton
            disableRipple
            aria-label='close'
            onClick={() => handleClose?.()}
            sx={(theme) => ({
              position: 'absolute',
              right: 11,
              top: 11,
            })}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='10'
              height='10'
              viewBox='0 0 10 10'
              fill='none'
            >
              <path
                d='M1.05542 0.94458L9.16626 9.05542M9.16626 0.94458L1.05542 9.05542'
                stroke='#56003D'
                strokeLinecap='round'
              />
            </svg>
          </IconButton>
        )}
        <DialogContent
          sx={{
            padding: '0',
          }}
        >
          {children}
        </DialogContent>
        {!hideActions && (
          <DialogActions
            sx={{
              padding: 0,
              mt: '16px',
            }}
          >
            <Button
              disableRipple
              disabled={formSubmited}
              sx={{
                width: '88px',
                height: '32px',
                background: '#EFEFEF',
                color: '#04002E',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 'normal',
                textTransform: 'capitalize',
                '&:hover': {
                  backgroundColor: '#EFEFEF',
                },
              }}
              onClick={() => handleClose?.()}
            >
              {formSubmited ? (
                <CircularProgress size={12} sx={{ color: '#04002E' }} />
              ) : (
                'Cancel'
              )}
            </Button>
            <Button
              disableRipple
              disabled={formSubmited}
              sx={{
                width: '110px',
                height: '32px',
                background: '#56003D',
                color: '#FFFFFF',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 'normal',
                textTransform: 'capitalize',
                ml: '12px !important',
                '&:hover': {
                  backgroundColor: '#56003D',
                },
              }}
              type='submit'
            >
              {formSubmited ? (
                <CircularProgress size={12} sx={{ color: '#FFFFFF' }} />
              ) : (
                'Continue'
              )}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
}

export default CustomDialog
